'use client'

import { useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'
import posthog from 'posthog-js'
import { safeGetSessionItem, safeSetSessionItem } from '@/lib/utils/safe-storage'

/**
 * Shared error-boundary effect: stale-deploy self-heal + observability capture.
 * Used by every `error.tsx` boundary (root + org segment) so nested boundaries
 * keep the same recovery semantics as the root one — a nested boundary catches
 * the error FIRST, so without this the chunk-reload self-heal would silently
 * stop applying to routes it covers.
 *
 * Stale-tab-after-deploy classes — both self-heal on a fresh fetch:
 *   - ChunkLoadError: old HTML asks for a chunk hashed for the previous
 *     build; Vercel returns 404 for that path on the new deployment.
 *   - UnrecognizedActionError: the encrypted Server Action ID embedded
 *     in the rendered HTML was retired by the new build's manifest.
 * Both are pure deploy churn; no user-visible signal needed.
 * SessionStorage guard prevents an infinite reload loop if the chunk
 * is genuinely broken (not just stale).
 */
export function useErrorBoundaryCapture(error: Error & { digest?: string }) {
  useEffect(() => {
    const isStaleDeployError =
      error.name === 'ChunkLoadError' ||
      /loading chunk \d+ failed|failed to load chunk/i.test(error.message) ||
      error.name === 'UnrecognizedActionError' ||
      /server action.*was not found on the server/i.test(error.message)

    if (isStaleDeployError && typeof window !== 'undefined') {
      // Shared with `instrumentation-client.ts` so a chunk-load that
      // already triggered a reload via the window listener doesn't
      // double-reload when the same error reaches the boundary.
      // Use safe wrappers — Safari with "Block all cookies" throws
      // ReferenceError on bare sessionStorage access, which would compound
      // the error inside the boundary and escape as an unhandled rejection.
      const reloadKey = 'alumnia:stale-deploy-reload'
      if (!safeGetSessionItem(reloadKey)) {
        safeSetSessionItem(reloadKey, '1')
        window.location.reload()
        return
      }
      // Already retried once — fall through to the normal error UI +
      // capture so a genuinely-broken chunk surfaces in observability.
    }

    Sentry.captureException(error)
    posthog.captureException(error)
  }, [error])
}
